import { StateCreator } from 'zustand';
import { debugLog, debugWarn } from '@/utils/debugLogger';
import { getCachedPosition, cachePosition, clearPositionCache } from '@/lib/geolocation/positionCache';
import { getCurrentPositionOptimized } from '@/lib/geolocation/getCurrentPosition';
import { toast } from 'sonner';

export interface GeolocationState {
  // Current position data
  position: GeolocationPosition | null;
  coordinates: { latitude: number; longitude: number } | null;
  geolocationError: GeolocationPositionError | null; // Renamed to avoid conflict with PriceState.error
  geolocationLoading: boolean; // Renamed to avoid conflict with PriceState.loading
  lastUpdated: number | null;
  
  // Request tracking to prevent duplicates
  requestInProgress: boolean;
  requestId: string | null;
  
  // Actions
  getCurrentPosition: (options?: GeolocationRequestOptions) => Promise<GeolocationPosition | null>;
  clearPosition: () => void;
  setGeolocationError: (error: GeolocationPositionError | null) => void;
  refreshPosition: () => Promise<void>;
  
  // Internal state management
  _setPosition: (position: GeolocationPosition | null) => void;
  _setGeolocationLoading: (loading: boolean) => void;
  _setRequestInProgress: (inProgress: boolean, requestId?: string | null) => void;
}

export interface GeolocationRequestOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
  showSuccessToast?: boolean;
  silentBackground?: boolean;
  forceRefresh?: boolean;
}

/**
 * Creates the geolocation slice for the Zustand store
 * Provides centralized geolocation state management to prevent duplicate GPS requests
 */
export const createGeolocationSlice: StateCreator<
  GeolocationState,
  [],
  [],
  GeolocationState
> = (set, get) => ({
  // Initial state
  position: null,
  coordinates: null,
  geolocationError: null,
  geolocationLoading: false,
  lastUpdated: null,
  requestInProgress: false,
  requestId: null,

  // Actions
  getCurrentPosition: async (options = {}) => {
    const state = get();
    const {
      enableHighAccuracy = true,
      timeout = 15000, // Reduced from 20s to 15s
      maximumAge = 300000, // 5 minutes in ms (was 15 minutes)
      showSuccessToast = false,
      silentBackground = false,
      forceRefresh = false
    } = options;

    // Generate unique request ID
    const requestId = Date.now().toString();

    try {
      // If forcing refresh, skip all cache checks and proceed directly to GPS request
      if (forceRefresh) {
        debugLog('general', 'Force refresh requested - bypassing all caches', { requestId }, 'GeolocationSlice');
      } else {
        // Check if request is already in progress
        if (state.requestInProgress) {
          debugLog('general', 'Geolocation request already in progress, skipping duplicate', { requestId: state.requestId }, 'GeolocationSlice');
          return state.position;
        }

        // Check store cache first
        if (state.position && state.lastUpdated) {
          const cacheAge = Date.now() - state.lastUpdated;
          if (cacheAge < maximumAge) {
            debugLog('general', 'Using store cached position', { cacheAge }, 'GeolocationSlice');
            return state.position;
          }
        }

        // Check localStorage cache
        const cachedPosition = getCachedPosition();
        if (cachedPosition) {
          debugLog('general', 'Using localStorage cached position', undefined, 'GeolocationSlice');
          get()._setPosition(cachedPosition);
          return cachedPosition;
        }
      }

      // Set loading state and mark request in progress
      get()._setGeolocationLoading(true);
      get()._setRequestInProgress(true, requestId);
      set({ geolocationError: null });

      debugLog('general', 'Starting new geolocation request', { requestId, options }, 'GeolocationSlice');

      // Request new position
      const position = await getCurrentPositionOptimized({
        enableHighAccuracy,
        timeout,
        maximumAge: forceRefresh ? 0 : maximumAge,
        showSuccessToast,
        silentBackground,
        useCache: !forceRefresh,
        onSuccess: (pos) => {
          debugLog('general', 'Geolocation success callback', { requestId }, 'GeolocationSlice');
        },
        onError: (error) => {
          debugWarn('general', 'Geolocation error callback', { requestId, error }, 'GeolocationSlice');
        }
      });

      // Update store with new position
      get()._setPosition(position);
      
      debugLog('general', 'Geolocation request completed successfully', { 
        requestId,
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy 
      }, 'GeolocationSlice');

      return position;

    } catch (error) {
      debugWarn('general', 'Geolocation request failed', { requestId, error }, 'GeolocationSlice');
      
      const geolocationError = error as GeolocationPositionError;
      set({ geolocationError });
      
      return null;
    } finally {
      get()._setGeolocationLoading(false);
      get()._setRequestInProgress(false, null);
    }
  },

  refreshPosition: async () => {
    debugLog('general', 'Refreshing position (forced)', undefined, 'GeolocationSlice');
    
    // Clear both caches before refreshing
    clearPositionCache();
    get().clearPosition();
    
    const position = await get().getCurrentPosition({ 
      forceRefresh: true, 
      showSuccessToast: true 
    });
    
    if (position) {
      toast.success('Posizione aggiornata con successo');
    }
  },

  clearPosition: () => {
    debugLog('general', 'Clearing position from store', undefined, 'GeolocationSlice');
    set({
      position: null,
      coordinates: null,
      geolocationError: null,
      lastUpdated: null,
      geolocationLoading: false,
      requestInProgress: false,
      requestId: null
    });
  },

  setGeolocationError: (error) => {
    set({ geolocationError: error, geolocationLoading: false, requestInProgress: false, requestId: null });
  },

  // Internal state management methods
  _setPosition: (position) => {
    if (position) {
      // Cache position in localStorage
      cachePosition(position);
      
      // Update store
      set({
        position,
        coordinates: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        },
        geolocationError: null,
        lastUpdated: Date.now()
      });
    } else {
      set({
        position: null,
        coordinates: null,
        lastUpdated: null
      });
    }
  },

  _setGeolocationLoading: (loading) => {
    set({ geolocationLoading: loading });
  },

  _setRequestInProgress: (inProgress, requestId = null) => {
    set({ requestInProgress: inProgress, requestId });
  }
});